import { gql } from '@apollo/client'
import { useContext, useEffect, useRef } from 'react'

import { useChooseFrequencyLaterEvents } from '../../../hooks/use-choose-frequency-later-events'
import { ChooseFrequencyLaterSteps } from '../../choose-frequency-later-steps'
import {
  ChooseFrequencyLaterStepsContent,
  StepContentWrapper,
} from '../../choose-frequency-later-steps/components'
import {
  ChooseFrequencyLaterContextProvider,
  useChooseOfferFrequencyLaterContext,
} from '../../choose-frequency-later-steps/context-provider'
import { ChooseFrequencyLaterModalSteps } from '../../choose-frequency-later-steps/types'
import { BoxLockContext } from '../../context'
import { ModalLock } from '../../dynamic-multiple-offers-switch-frequency-lock/components'
import { PaywallLock } from '../../paywall-lock'
import {
  Upgrade2StepCurrentLayoutLock_OfferLockDataFragment,
  Upgrade2StepCurrentLayoutLock_ViewerFragment,
} from './__generated__/current-layout.graphql'

export interface Upgrade2StepCurrentLayoutLockProps {
  offerLockData: Upgrade2StepCurrentLayoutLock_OfferLockDataFragment
  viewer: Upgrade2StepCurrentLayoutLock_ViewerFragment
}

export function Upgrade2StepCurrentLayoutLock({
  offerLockData,
  viewer,
}: Upgrade2StepCurrentLayoutLockProps) {
  const { componentType } = useContext(BoxLockContext)

  if (componentType === 'paywall') {
    return (
      <PaywallLock offerLockData={offerLockData}>
        <ChooseFrequencyLaterSteps
          offerLockData={offerLockData}
          viewer={viewer}
          componentType="paywall"
        />
      </PaywallLock>
    )
  }

  return (
    <ChooseFrequencyLaterContextProvider offerLockData={offerLockData} componentType="modal">
      <Upgrade2StepModalContent offerLockData={offerLockData} viewer={viewer} />
    </ChooseFrequencyLaterContextProvider>
  )
}

function Upgrade2StepModalContent({ offerLockData, viewer }: Upgrade2StepCurrentLayoutLockProps) {
  const { currentStep } = useChooseOfferFrequencyLaterContext()
  const { sendModalClosedEvent } = useChooseFrequencyLaterEvents()
  const isSecondStep = currentStep === ChooseFrequencyLaterModalSteps.SELECT_FREQUENCY

  const cards = offerLockData?.body?.cards || []
  const hasMultiplePlans = new Set(cards.map((c) => c.title)).size > 1

  const currentStepRef = useRef(currentStep)
  currentStepRef.current = currentStep

  useEffect(() => {
    return () => {
      if (cards[0]) sendModalClosedEvent(currentStepRef.current, cards[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const modalSize = hasMultiplePlans ? 'md' : 'sm'

  const modalOfferLockData = isSecondStep
    ? {
        ...offerLockData,
        header: {
          ...offerLockData.header,
          title: offerLockData.header?.secondStepTitle || offerLockData.header?.title,
        },
      }
    : offerLockData

  return (
    <ModalLock offerLockData={modalOfferLockData} size={modalSize} headingSize="lg">
      <StepContentWrapper>
        <ChooseFrequencyLaterStepsContent offerLockData={offerLockData} viewer={viewer} />
      </StepContentWrapper>
    </ModalLock>
  )
}

Upgrade2StepCurrentLayoutLock.fragments = {
  offerLockData: gql`
    fragment Upgrade2StepCurrentLayoutLock_offerLockData on OfferLockData {
      header {
        title
        secondStepTitle
      }
      ...ModalLock_offerLockData
      ...PaywallLock_offerLockData
      ...ChooseFrequencyLaterSteps_offerLockData
    }
    ${ModalLock.fragments.offerLockData}
    ${PaywallLock.fragments.offerLockData}
    ${ChooseFrequencyLaterSteps.fragments.offerLockData}
  `,
  viewer: gql`
    fragment Upgrade2StepCurrentLayoutLock_viewer on Profile {
      ...ChooseFrequencyLaterSteps_viewer
    }
    ${ChooseFrequencyLaterSteps.fragments.viewer}
  `,
}
